import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { createPortalApi } from '../../api/portalApi';
import { useRuntimeConfig } from '../../context/RuntimeConfigContext';
import { useUserRole } from '../../context/UserRoleContext';
import { Icon } from '../../components/icons';
import type { OrgSettings, UserAccess, Project } from '../../types';

const TARGET_LABEL: Record<string, string> = {
  jira: 'Jira', ado: 'Azure DevOps', zephyr: 'Zephyr Scale', testlink: 'TestLink',
};

const PROVIDER_LABEL: Record<string, string> = {
  claude: 'Claude (Anthropic)', openai: 'OpenAI', groq: 'Groq', azureopenai: 'Azure OpenAI',
};

export default function SettingsHub() {
  const { isAdmin } = useUserRole();
  const { instance } = useMsal();
  const cfg = useRuntimeConfig();
  const api = createPortalApi(instance, cfg.msalClientId, cfg.apiBaseUrl);
  const navigate = useNavigate();

  const [settings, setSettings]   = useState<OrgSettings | null>(null);
  const [users, setUsers]         = useState<UserAccess[] | null>(null);
  const [projects, setProjects]   = useState<Project[] | null>(null);

  useEffect(() => {
    // Projects are shared org-wide but open to every approved member, not just admins.
    api.getProjects().then(setProjects).catch(() => {});
    if (isAdmin) {
      api.getSettings().then(setSettings).catch(() => {});
      api.getAdminUsers().then(setUsers).catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const pendingCount = users?.filter(u => u.status === 'pending').length ?? 0;
  const activeProjectCount = projects?.filter(p => p.status === 'active').length ?? 0;

  const projectsCard = {
    key: 'projects',
    icon: 'layers',
    title: 'Projects',
    desc: 'Give the AI generator durable knowledge of what a product is, so output is grounded in real context.',
    status: projects ? `${activeProjectCount} active project${activeProjectCount === 1 ? '' : 's'}` : undefined,
  };

  const cards: Array<{ key: string; icon: string; title: string; desc: string; status?: string }> = isAdmin
    ? [
        {
          key: 'push-target',
          icon: 'send',
          title: 'Push Target',
          desc: 'Where approved test cases are pushed — Jira, Azure DevOps, Zephyr Scale, or TestLink.',
          status: settings ? `Currently: ${TARGET_LABEL[settings.targetType] ?? settings.targetType}` : undefined,
        },
        {
          key: 'llm',
          icon: 'cpu',
          title: 'LLM Configuration',
          desc: 'Organisation-wide AI provider and model, duplicate-detection embeddings, and Azure AI Search.',
          status: settings?.llmProvider ? `Currently: ${PROVIDER_LABEL[settings.llmProvider] ?? settings.llmProvider}` : undefined,
        },
        {
          key: 'git',
          icon: 'git',
          title: 'Git Integration',
          desc: "Repository the Web Automation Coder's \"Push to repo\" button pushes generated scripts to.",
          status: settings?.githubOwner && settings?.githubRepo ? `${settings.githubOwner}/${settings.githubRepo}` : undefined,
        },
        {
          key: 'users',
          icon: 'users',
          title: 'User Management',
          desc: 'Approve or reject access requests and assign roles to your team.',
          status: users ? `${users.length} user${users.length === 1 ? '' : 's'}${pendingCount ? ` · ${pendingCount} pending` : ''}` : undefined,
        },
        projectsCard,
      ]
    : [projectsCard];

  return (
    <div className="settings-hub-grid">
      {cards.map(c => (
        <button
          key={c.key}
          type="button"
          className="settings-hub-card"
          onClick={() => navigate(`/settings/${c.key}`)}
        >
          <span className="settings-hub-card-icon"><Icon name={c.icon} size={22} /></span>
          <span className="settings-hub-card-title">{c.title}</span>
          <span className="settings-hub-card-desc">{c.desc}</span>
          <span className="settings-hub-card-status">{c.status ?? ' '}</span>
        </button>
      ))}
    </div>
  );
}
